<ng-template #template>{{message}}</ng-template>

<div class="clipboard-page">
  <!-- Header -->
  <div class="flex items-center justify-between flex-wrap gap-4 mb-8">
    <div>
      <h1 class="text-2xl font-bold text-slate-900 tracking-tight hidden">Clipboard</h1>
      <p class="text-sm text-slate-500 mt-1">CTRL-V from Flow Designer's Copy to My Clipboard. We'll store them for you.</p>
      <p class="text-xs text-slate-400 mt-1">Note: This feature is for the classic Power Automate portal.</p>
    </div>
    <a href="https://learn.flowstudio.app/clipboard" target="learn"
      class="inline-flex items-center gap-2 text-sm font-medium text-brand hover:underline">
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg>
      Learn about Clipboard
    </a>
  </div>

  <!-- Search -->
  <div class="relative mb-6">
    <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
    <input type="text" [(ngModel)]="this.searchTerm"
      placeholder="Search snippets..."
      class="w-full pl-10 pr-4 py-2.5 text-sm rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-brand"
      style="border: 1px solid #e2e8f0;" />
  </div>

  <!-- Personal Clipboard -->
  <section class="mb-10">
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-semibold text-slate-800">Personal Clipboard</h2>
      <div class="flex items-center gap-3">
        <span class="text-xs text-slate-400">{{this.filteredClips.length}} snippet{{this.filteredClips.length !== 1 ? 's' : ''}}</span>
        <button (click)="this.exportClips()"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white rounded-lg shadow hover:bg-slate-50 border-0 cursor-pointer transition-colors"
          title="Export personal clips to JSON">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
          Export
        </button>
        <label
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white rounded-lg shadow hover:bg-slate-50 cursor-pointer transition-colors">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
          Import
          <input type="file" accept=".json" (change)="this.importClips($event)" class="hidden" />
        </label>
      </div>
    </div>

    @if (this.filteredClips.length > 0) {
      <div class="space-y-3">
        @for (clip of this.filteredClips; track clip) {
          <div
            class="flex items-center gap-4 bg-white rounded-xl px-4 py-2.5 shadow-sm hover:shadow-md transition-shadow">
            <div class="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden"
              [style.background-color]="clip.brandColor + '18'">
              <img [src]="this.safeUrl(clip.icon)" class="w-7 h-7" />
            </div>
            <div class="flex-grow min-w-0">
              <div class="font-semibold text-sm text-slate-900 truncate">{{clip.operationName}}</div>
              <div class="text-xs text-slate-500 truncate">{{clip.connectorDisplayName}}</div>
            </div>
            @if (clip.operationDefinition?.description) {
              <div
                class="shrink-0 text-slate-400 hover:text-slate-600 cursor-help"
                [title]="clip.operationDefinition.description">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              </div>
            }
            <div class="shrink-0 flex items-center gap-1">
              <button [clipboard-copy]="this.stringify(clip)" (copied)="this.copied(clip)"
                class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-brand hover:bg-brand-light border-0 cursor-pointer transition-colors"
                style="background: transparent;" title="Copy to clipboard">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
              </button>
              <button (click)="this.removeClip(clip)"
                class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 border-0 cursor-pointer transition-colors"
                style="background: transparent;" title="Remove">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
              </button>
            </div>
          </div>
        }
      </div>
    }

    @if (this.filteredClips.length === 0) {
      <div
        class="text-center py-12 bg-white rounded-xl shadow-sm">
        <svg class="w-10 h-10 text-slate-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
        <p class="text-sm text-slate-500">No clips yet. Use CTRL-V to paste from Flow Designer.</p>
      </div>
    }
  </section>

  <!-- Community Clipboard -->
  <section>
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-semibold text-slate-800">Community Clipboard</h2>
      <span class="text-xs text-slate-400">{{this.filteredCommunity.length + this.filteredSubscriptions.length}} snippet{{(this.filteredCommunity.length + this.filteredSubscriptions.length) !== 1 ? 's' : ''}}</span>
    </div>

    <div class="space-y-3">
      @for (clip of this.filteredCommunity; track clip) {
        <div
          class="flex items-center gap-4 bg-white rounded-xl px-4 py-2.5 shadow-sm hover:shadow-md transition-shadow">
          <div class="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden"
            [style.background-color]="clip.brandColor + '18'">
            <img [src]="this.safeUrl(clip.icon)" class="w-7 h-7" />
          </div>
          <div class="flex-grow min-w-0">
            <div class="font-semibold text-sm text-slate-900 truncate">{{clip.operationName}}</div>
            <div class="text-xs text-slate-500 truncate">
              {{clip.connectorDisplayName}}
              @if (clip.url) {
                <a [href]="clip.url" target="_blank"
                  class="ml-2 text-brand font-medium hover:underline">
                  {{clip.credit}}
                </a>
              }
            </div>
          </div>
          @if (clip.operationDefinition?.description) {
            <div
              class="shrink-0 text-slate-400 hover:text-slate-600 cursor-help"
              [title]="clip.operationDefinition.description">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            </div>
          }
          <div class="shrink-0 flex items-center gap-1">
            <button [clipboard-copy]="this.stringify(clip)" (copied)="this.copied(clip)"
              class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-brand hover:bg-brand-light border-0 cursor-pointer transition-colors"
              style="background: transparent;" title="Copy to clipboard">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
            </button>
          </div>
        </div>
      }

      @for (clip of this.filteredSubscriptions; track clip) {
        <div
          class="flex items-center gap-4 bg-white rounded-xl px-4 py-2.5 shadow-sm hover:shadow-md transition-shadow">
          <div class="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden"
            [style.background-color]="clip.brandColor + '18'">
            <img [src]="this.safeUrl(clip.icon)" class="w-7 h-7" />
          </div>
          <div class="flex-grow min-w-0">
            <div class="font-semibold text-sm text-slate-900 truncate">{{clip.operationName}}</div>
            <div class="text-xs text-slate-500 truncate">
              {{clip.connectorDisplayName}}
              @if (clip.url) {
                <a [href]="clip.url" target="_blank"
                  class="ml-2 text-brand font-medium hover:underline">
                  {{clip.credit}}
                </a>
              }
            </div>
          </div>
          @if (clip.operationDefinition?.description) {
            <div
              class="shrink-0 text-slate-400 hover:text-slate-600 cursor-help"
              [title]="clip.operationDefinition.description">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            </div>
          }
          <div class="shrink-0 flex items-center gap-1">
            <button [clipboard-copy]="this.stringify(clip)" (copied)="this.copied(clip)"
              class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-brand hover:bg-brand-light border-0 cursor-pointer transition-colors"
              style="background: transparent;" title="Copy to clipboard">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
            </button>
          </div>
        </div>
      }
    </div>
  </section>
</div>
